import React, { useState, useEffect } from "react";
import { User } from "firebase/auth";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import PageLoader from "../components/PageLoader";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { fetchCompetitions, Competition, Gameweek, capForMode } from "../api/competitions";
import { createLeague } from "../api/leagues";
import {
  card, narrowPageContainer, primaryBtn,
  inputStyle, selectStyle, errorStyle, badge,
} from "../styles";
import { useIsMobile } from "../hooks/useIsMobile";
import HowToPlayModal from "../components/HowToPlayModal";
import {
  LeagueMode,
  ALL_LEAGUE_MODES,
  MODE_CONTENT,
  MODE_COLOR_VAR,
  modeHasConfig,
  modeHasStartGw,
  effortTier,
} from "./createLeague/modeContent";

// Export constants used by tests for exhaustiveness checks
export { ALL_LEAGUE_MODES, MODE_CONTENT };

type WizardStep = 1 | 2 | 3;

interface Props { user: User; }

function getAvailableStartGameweeks(gameweeks: Gameweek[], cap: number | null): Gameweek[] {
  return gameweeks.filter((gw) => !gw.isStarted && (cap === null || gw.number <= cap));
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CreateLeaguePage({ user }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  useBreadcrumbs([{ label: t("common.home"), to: "/overview" }, { label: t("createLeague.title") }]);

  // ── Data ──
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [compLoading, setCompLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const delayedLoading = useDelayedLoading(compLoading);

  // ── Wizard state ──
  const [step, setStep] = useState<WizardStep>(1);

  // ── Selections ──
  const [previewMode, setPreviewMode] = useState<LeagueMode | null>(null);
  const [selectedMode, setSelectedMode] = useState<LeagueMode | null>(null);
  const [howToPlayMode, setHowToPlayMode] = useState<LeagueMode | null>(null);
  const modeColor = selectedMode ? MODE_COLOR_VAR[selectedMode] : "var(--color-accent)";
  const [competitionId, setCompetitionId] = useState("");
  const [showCompPicker, setShowCompPicker] = useState(false);
  const [startGameweekId, setStartGameweekId] = useState("");
  const [target, setTarget] = useState(21);
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ── Fetch ──
  function doFetch() {
    setFetchError(false);
    setCompLoading(true);
    fetchCompetitions(user)
      .then((data) => setCompetitions(data))
      .catch(() => setFetchError(true))
      .finally(() => setCompLoading(false));
  }

  useEffect(() => { doFetch(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived ──
  const selectedComp = competitions.find((c) => c.id === competitionId);
  const compatibleComps = selectedMode
    ? competitions.filter((c) => c.creatableModes.includes(selectedMode))
    : [];
  const availableStartGws = selectedComp && selectedMode
    ? getAvailableStartGameweeks(selectedComp.gameweeks, capForMode(selectedComp.modeJoinCaps, selectedMode))
    : [];

  // Available-first sort for the accordion
  const sortedModes: LeagueMode[] = [
    ...ALL_LEAGUE_MODES.filter((m) => competitions.some((c) => c.creatableModes.includes(m))),
    ...ALL_LEAGUE_MODES.filter((m) => !competitions.some((c) => c.creatableModes.includes(m))),
  ];

  const anyCreatable = sortedModes.some((m) => competitions.some((c) => c.creatableModes.includes(m)));

  const step1Valid = !!selectedMode && competitions.some((c) => c.creatableModes.includes(selectedMode));
  const step2Valid = !!competitionId && !!name.trim();

  // ── Auto-resolve competition when mode changes ──
  useEffect(() => {
    if (!selectedMode) return;
    const compatible = competitions.filter((c) => c.creatableModes.includes(selectedMode));
    setTarget(21);
    setStartGameweekId("");
    if (compatible.length === 1) {
      const comp = compatible[0];
      setCompetitionId(comp.id);
      setShowCompPicker(false);
      const avail = getAvailableStartGameweeks(comp.gameweeks, capForMode(comp.modeJoinCaps, selectedMode));
      if (avail.length > 0) setStartGameweekId(avail[0].id);
    } else {
      setCompetitionId("");
      setShowCompPicker(compatible.length > 1);
    }
  }, [selectedMode, competitions]);

  // ── Competition selection ──
  function handleSelectCompetition(id: string) {
    const comp = competitions.find((c) => c.id === id);
    if (!comp || !selectedMode) return;
    setCompetitionId(id);
    setShowCompPicker(false);
    const avail = getAvailableStartGameweeks(comp.gameweeks, capForMode(comp.modeJoinCaps, selectedMode));
    if (avail.length > 0) setStartGameweekId(avail[0].id);
  }

  // ── Mode accordion click ──
  function handleModeSelect(mode: LeagueMode) {
    const available = competitions.some((c) => c.creatableModes.includes(mode));
    setPreviewMode((prev) => (prev === mode ? null : mode));
    if (available) {
      setSelectedMode(mode);
      setError("");
    }
  }

  // Arrow-key navigation within the accordion list
  function handleAccordionKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const modes = sortedModes;
    const currentIdx = previewMode ? modes.indexOf(previewMode) : 0;
    let nextIdx = currentIdx;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      nextIdx = (currentIdx + 1) % modes.length;
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      nextIdx = (currentIdx - 1 + modes.length) % modes.length;
    } else {
      return;
    }
    handleModeSelect(modes[nextIdx]);
  }

  // ── Wizard navigation ──
  function goNext() {
    if (step === 1 && step1Valid) setStep(2);
    if (step === 2) {
      if (!name.trim()) { setNameTouched(true); return; }
      if (step2Valid) setStep(3);
    }
  }

  function goBack() {
    if (step > 1) setStep((s) => (s - 1) as WizardStep);
  }

  // ── Submit ──
  async function handleSubmit() {
    setError("");
    if (!name.trim()) { setNameTouched(true); return; }
    if (!selectedMode || !competitionId) return;
    setSubmitting(true);
    try {
      const league = await createLeague(user, {
        name: name.trim(),
        competitionId,
        mode: selectedMode,
        ...(selectedMode === "TARGET" ? { target } : {}),
        ...(modeHasStartGw(selectedMode) && startGameweekId ? { startGameweekId } : {}),
      });
      navigate(`/leagues/${league.id}`, { state: { justCreated: true } });
    } catch (err: any) {
      setError(err?.message || t("createLeague.errors.failed"));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Shared style helpers ──
  const eyebrow: React.CSSProperties = {
    fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.1em",
    textTransform: "uppercase", color: "var(--color-text-dim)",
    marginBottom: "0.5rem",
  };

  // ── Selectable card button factory ──
  function selectableCard(selected: boolean, onClick: () => void, children: React.ReactNode, extra?: React.CSSProperties): React.ReactElement {
    return (
      <button
        onClick={onClick}
        style={{
          display: "flex", alignItems: "flex-start", gap: "0.875rem",
          padding: "1rem", borderRadius: 10, width: "100%",
          border: `1px solid ${selected ? modeColor : "var(--color-border-subtle)"}`,
          background: selected ? `color-mix(in srgb, ${modeColor} 15%, transparent)` : "transparent",
          cursor: "pointer", textAlign: "left",
          transition: "border-color 0.15s, background 0.15s",
          ...extra,
        } as React.CSSProperties}
        onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (!selected) {
            (e.currentTarget as HTMLElement).style.background = "var(--color-surface-hover)";
            (e.currentTarget as HTMLElement).style.borderColor = "var(--color-border)";
          }
        }}
        onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (!selected) {
            (e.currentTarget as HTMLElement).style.background = "transparent";
            (e.currentTarget as HTMLElement).style.borderColor = "var(--color-border-subtle)";
          }
        }}
      >
        {children}
      </button>
    );
  }

  // ── Render: stepper ──
  function renderStepper(): React.ReactElement {
    const steps: Array<{ key: string; label: string }> = [
      { key: "stepMode", label: t("createLeague.stepMode") },
      { key: "stepSettings", label: t("createLeague.stepSettings") },
      { key: "stepReview", label: t("createLeague.stepReview") },
    ];
    // Drop labels below ~360px to avoid overflow
    const showLabels = !isMobile || (typeof window !== "undefined" && window.innerWidth > 360);

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: "1.5rem" }}>
        {steps.map(({ label }, i) => {
          const sn = i + 1 as WizardStep;
          const done = step > sn;
          const active = step === sn;
          return (
            <React.Fragment key={sn}>
              <div style={{ display: "flex", flexDirection: "column" as const, alignItems: "center", gap: "0.3rem" }}>
                <div
                  style={{
                    width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: done
                      ? "var(--color-accent)"
                      : active
                      ? "var(--color-accent-glow)"
                      : "var(--color-surface-alt)",
                    border: `2px solid ${done || active ? "var(--color-accent)" : "var(--color-border)"}`,
                    transition: "all 0.2s",
                  }}
                >
                  {done ? (
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--color-btn-primary-text)" strokeWidth="2.5">
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span
                      style={{
                        fontFamily: "var(--font-display)", fontSize: "0.9rem", fontWeight: 700,
                        color: active ? "var(--color-accent)" : "var(--color-text-dim)",
                      }}
                    >
                      {sn}
                    </span>
                  )}
                </div>
                {showLabels && (
                  <span
                    style={{
                      fontSize: "0.68rem", fontWeight: active ? 600 : 400, whiteSpace: "nowrap" as const,
                      color: done ? "var(--color-accent)" : active ? "var(--color-text)" : "var(--color-text-dim)",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {label}
                  </span>
                )}
              </div>
              {i < steps.length - 1 && (
                <div
                  style={{
                    flex: 1, height: 2,
                    marginBottom: showLabels ? "1.25rem" : 0,
                    marginLeft: "0.4rem", marginRight: "0.4rem",
                    background: step > sn ? "var(--color-accent)" : "var(--color-border-subtle)",
                    transition: "background 0.3s",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // ── Render: accordion row for mode picker ──
  function renderAccordionRow(mode: LeagueMode, idx: number): React.ReactElement {
    const content = MODE_CONTENT[mode];
    const Icon = content.icon;
    const isOpen = previewMode === mode;
    const isSelected = selectedMode === mode;
    const available = competitions.some((c) => c.creatableModes.includes(mode));
    const keyRoot = content.keyRoot;
    const modeColor = MODE_COLOR_VAR[mode];
    const panelId = `mode-panel-${mode}`;
    const headerId = `mode-header-${mode}`;

    // Combined chip text: "One-off · Low effort" etc.
    const cadenceLabel = content.engagement === "FIRE_AND_FORGET"
      ? t("createLeague.engagementOneOff")
      : t("createLeague.engagementWeekly");
    const tier = effortTier(content.effort);
    const effortLabel = t(`createLeague.effort.${tier}`);

    return (
      <div
        key={mode}
        role="listitem"
        style={{
          borderRadius: 10,
          border: `1px solid ${isSelected && available ? modeColor : "var(--color-border-subtle)"}`,
          background: isSelected && available ? `color-mix(in srgb, ${modeColor} 15%, transparent)` : "var(--color-surface)",
          transition: "border-color 0.15s, background 0.15s",
          overflow: "hidden",
          opacity: !available ? 0.55 : 1,
        }}
      >
        {/* Header row (button) */}
        <button
          id={headerId}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => handleModeSelect(mode)}
          tabIndex={idx === 0 || isOpen ? 0 : -1}
          style={{
            display: "flex", alignItems: "center", gap: "0.75rem",
            padding: "0.875rem 1rem", width: "100%",
            background: "none", border: "none", cursor: "pointer",
            textAlign: "left" as const, borderRadius: 0,
          }}
          onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
            if (!isSelected || !available) {
              (e.currentTarget as HTMLElement).style.background = "var(--color-surface-hover)";
            }
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
            (e.currentTarget as HTMLElement).style.background = "none";
          }}
        >
          {/* Icon box — always tinted with the mode's accent, like the landing page, so the
              glyph + color pairing stays recognisable whether or not the row is selected */}
          <div
            style={{
              width: 38, height: 38, borderRadius: 8, flexShrink: 0,
              background: available
                ? `color-mix(in srgb, ${modeColor} ${isSelected ? 20 : 12}%, transparent)`
                : "var(--color-surface-alt)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: available ? modeColor : "var(--color-text-dim)",
              transition: "all 0.15s",
            }}
          >
            <Icon />
          </div>
          {/* Name + tagline */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.2,
                color: isSelected && available ? modeColor : "var(--color-text)",
              }}
            >
              {t(`${keyRoot}.label`)}
              {isSelected && available && (
                <span style={{ position: "absolute" as const, left: "-9999px" }}> (selected)</span>
              )}
            </div>
            <div style={{ fontSize: "0.78rem", color: "var(--color-text-muted)", marginTop: "0.1rem", lineHeight: 1.35 }}>
              {t(`${keyRoot}.tagline`)}
            </div>
          </div>
          {/* Lock for unavailable */}
          {!available && (
            <span aria-hidden style={{ fontSize: "0.8rem", flexShrink: 0, opacity: 0.7 }}>🔒</span>
          )}
          {/* Chevron */}
          <svg
            width={14} height={14} viewBox="0 0 24 24"
            fill="none" stroke="var(--color-text-muted)" strokeWidth={2}
            style={{
              flexShrink: 0,
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {/* Detail panel — always mounted, grid-rows transition (avoids max-height white sliver) */}
        <div
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          style={{
            display: "grid",
            gridTemplateRows: isOpen ? "1fr" : "0fr",
            transition: "grid-template-rows 0.25s ease",
          }}
        >
          <div style={{ overflow: "hidden" }}>
            <div
              style={{
                padding: "0 1rem 1rem",
                borderTop: "1px solid var(--color-border-subtle)",
                paddingTop: "0.875rem",
                marginTop: "0",
              }}
            >
              {/* Paragraph */}
              <p style={{ margin: "0 0 0.5rem", fontSize: "0.82rem", color: "var(--color-text-muted)", lineHeight: 1.5 }}>
                {t(`${keyRoot}.paragraph`)}
              </p>
              {/* "You win by…" line */}
              <p style={{ margin: "0 0 0.75rem", fontSize: "0.82rem", color: "var(--color-text)", lineHeight: 1.4 }}>
                <strong>{t("createLeague.winByLabel")}</strong>{" " + t(`${keyRoot}.winBy`)}
              </p>
              {/* How to play */}
              <button
                onClick={(e) => { e.stopPropagation(); setHowToPlayMode(mode); }}
                style={{
                  background: "none", border: "none", padding: 0, marginBottom: "0.75rem",
                  font: "inherit", fontSize: "0.8rem", fontWeight: 600, color: modeColor,
                  cursor: "pointer", textDecoration: "underline", display: "block",
                }}
              >
                {t("createLeague.howToPlayCta")}
              </button>
              {/* Engagement + effort pills */}
              {available && (
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: "0.4rem" }}>
                  {[cadenceLabel, effortLabel].map((text) => (
                    <span
                      key={text}
                      style={{
                        ...badge,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase" as const,
                        color: modeColor,
                        border: `1px solid ${modeColor}`,
                        background: `color-mix(in srgb, ${modeColor} 12%, transparent)`,
                      }}
                    >
                      {text}
                    </span>
                  ))}
                </div>
              )}
              {/* Not available banner */}
              {!available && (
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    padding: "0.5rem 0.75rem", borderRadius: 8,
                    background: "var(--color-surface-alt)",
                    border: "1px solid var(--color-border-subtle)",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.08em",
                      textTransform: "uppercase" as const,
                      background: "var(--color-surface)", color: "var(--color-text-dim)",
                      padding: "0.2rem 0.5rem", borderRadius: 4, flexShrink: 0,
                    }}
                  >
                    {t("createLeague.notAvailable.banner")}
                  </span>
                  <Link
                    to="/about#mode-availability"
                    style={{
                      color: "var(--color-accent)", fontSize: "0.78rem", fontWeight: 600,
                      textDecoration: "underline", textUnderlineOffset: 2, flexShrink: 0,
                    }}
                  >
                    {t("createLeague.notAvailable.whyLink")}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Step 1 — Mode ──
  function renderStep1(): React.ReactElement {
    return (
      <div>
        <div style={{ ...eyebrow }}>{t("createLeague.step1.eyebrow")}</div>
        <h2
          style={{
            fontFamily: "var(--font-display)", fontSize: isMobile ? "1.4rem" : "1.7rem",
            fontWeight: 800, color: "var(--color-text)", margin: "0 0 1.25rem",
          }}
        >
          {t("createLeague.step1.heading")}
        </h2>
        {/* Off-season banner */}
        {!anyCreatable && (
          <div
            style={{
              padding: "0.75rem 1rem", borderRadius: 8, marginBottom: "1rem",
              background: "var(--color-surface-alt)",
              border: "1px solid var(--color-border-subtle)",
              fontSize: "0.82rem", color: "var(--color-text-muted)", lineHeight: 1.4,
            }}
          >
            {t("createLeague.step1.offSeasonBanner")}
          </div>
        )}
        {/* Accordion list */}
        <div
          role="list"
          aria-label={t("createLeague.modePickerLabel")}
          onKeyDown={handleAccordionKeyDown}
          style={{ display: "flex", flexDirection: "column" as const, gap: "0.5rem" }}
        >
          {sortedModes.map((mode, idx) => renderAccordionRow(mode, idx))}
        </div>
      </div>
    );
  }

  // ── Render: competition section ──
  function renderCompetition(): React.ReactElement | null {
    if (!selectedMode) return null;
    const compatible = compatibleComps;

    // Auto-resolved: show summary
    if (competitionId && !showCompPicker) {
      const comp = competitions.find((c) => c.id === competitionId);
      if (!comp) return null;
      return (
        <div style={{ marginBottom: "1.25rem" }}>
          <div style={{ ...eyebrow }}>{t("createLeague.competition.autoSelectedLabel")}</div>
          <div
            style={{
              ...card,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "0.875rem 1rem",
              border: `1px solid ${modeColor}`,
              background: `color-mix(in srgb, ${modeColor} 15%, transparent)`,
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.9rem", color: modeColor }}>{comp.name}</div>
              <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginTop: "0.15rem" }}>{comp.season}</div>
            </div>
            {compatible.length > 1 ? (
              <button
                onClick={() => { setCompetitionId(""); setShowCompPicker(true); }}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: modeColor, fontSize: "0.8rem", fontWeight: 600,
                  padding: "0.25rem 0.5rem", borderRadius: 6, fontFamily: "inherit",
                }}
                onMouseEnter={(e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.background = `color-mix(in srgb, ${modeColor} 15%, transparent)`; }}
                onMouseLeave={(e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.background = "none"; }}
              >
                {t("createLeague.competition.changeBtn")}
              </button>
            ) : null}
          </div>
          {/* Teaser card when only one competition available */}
          {compatible.length === 1 && (
            <div
              style={{
                ...card,
                display: "flex", alignItems: "center", gap: "0.75rem",
                padding: "0.875rem 1rem",
                marginTop: "0.5rem",
                border: "1px dashed var(--color-border-subtle)",
                background: "var(--color-surface-raised)",
                opacity: 0.6,
                cursor: "default",
              }}
            >
              <div
                style={{
                  width: 32, height: 32, borderRadius: 6, flexShrink: 0,
                  background: "var(--color-border-subtle)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.85rem",
                }}
              >
                🔒
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--color-text-muted)" }}>
                  {t("createLeague.step2.moreComingSoon")}
                </div>
                <div style={{ fontSize: "0.73rem", color: "var(--color-text-dim)", marginTop: "0.1rem" }}>
                  {t("createLeague.step2.moreComingSoonSub")}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Competition picker
    if (showCompPicker) {
      const groups: { labelKey: string; type: string }[] = [
        { labelKey: "createLeague.competition.groupInternational", type: "INTERNATIONAL" },
        { labelKey: "createLeague.competition.groupClub", type: "CLUB" },
      ];
      return (
        <div style={{ marginBottom: "1.25rem" }}>
          <div style={{ ...eyebrow }}>{t("createLeague.competition.heading")}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {groups.map(({ labelKey, type }) => {
              const comps = compatible.filter((c) => c.type === type);
              if (comps.length === 0) return null;
              return (
                <div key={type}>
                  <div
                    style={{
                      fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.12em",
                      textTransform: "uppercase" as const, color: "var(--color-text-dim)",
                      marginBottom: "0.5rem", paddingLeft: "0.25rem",
                    }}
                  >
                    {t(labelKey)}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.5rem" }}>
                    {comps.map((c) => {
                      const isSel = competitionId === c.id;
                      return React.cloneElement(
                        selectableCard(
                          isSel,
                          () => handleSelectCompetition(c.id),
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "0.875rem", color: isSel ? modeColor : "var(--color-text)", marginBottom: "0.2rem" }}>{c.name}</div>
                            <div style={{ fontSize: "0.72rem", color: "var(--color-text-dim)" }}>{c.season}</div>
                          </div>,
                          { display: "block" },
                        ),
                        { key: c.id },
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  }

  // ── Render: config section ──
  function renderConfig(): React.ReactElement | null {
    if (!selectedMode || !competitionId) return null;
    if (!modeHasConfig(selectedMode) && !modeHasStartGw(selectedMode)) return null;
    const fieldBox: React.CSSProperties = {
      ...card, marginBottom: "1.25rem",
      border: "1px solid var(--color-border-subtle)", padding: "1.25rem",
    };

    return (
      <>
        {/* TARGET: stepper + live example — its own boxed field */}
        {selectedMode === "TARGET" && (
          <div style={fieldBox}>
            <div style={{ ...eyebrow }}>{t("createLeague.config.target.label")}</div>
            <p style={{ margin: "0 0 0.75rem", fontSize: "0.82rem", color: "var(--color-text-muted)", lineHeight: 1.5 }}>
              {t("createLeague.config.target.desc")}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: "0.6rem" }}>
              <button
                onClick={() => setTarget((v) => Math.max(1, v - 1))}
                style={{
                  width: 44, height: 44, borderRadius: "8px 0 0 8px",
                  border: "1px solid var(--color-border)", borderRight: "none",
                  background: "var(--color-surface-alt)", cursor: "pointer",
                  fontSize: "1.1rem", fontWeight: 600, color: "var(--color-text)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
                onMouseEnter={(e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.background = "var(--color-surface-hover)"; }}
                onMouseLeave={(e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.background = "var(--color-surface-alt)"; }}
              >
                −
              </button>
              <div
                style={{
                  width: 64, height: 44,
                  border: "1px solid var(--color-border)",
                  background: "var(--color-surface)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "var(--font-display)", fontSize: "1.25rem", fontWeight: 700,
                  color: "var(--color-text)", userSelect: "none" as const,
                }}
              >
                {target}
              </div>
              <button
                onClick={() => setTarget((v) => Math.min(999, v + 1))}
                style={{
                  width: 44, height: 44, borderRadius: "0 8px 8px 0",
                  border: "1px solid var(--color-border)", borderLeft: "none",
                  background: "var(--color-surface-alt)", cursor: "pointer",
                  fontSize: "1.1rem", fontWeight: 600, color: "var(--color-text)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
                onMouseEnter={(e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.background = "var(--color-surface-hover)"; }}
                onMouseLeave={(e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.background = "var(--color-surface-alt)"; }}
              >
                +
              </button>
            </div>
            <p style={{ margin: "0 0 0", fontSize: "0.78rem", color: "var(--color-text-dim)", lineHeight: 1.4 }}>
              {t("createLeague.config.target.liveExample", { target, bust: target + 1 })}
            </p>
          </div>
        )}

        {/* Start GW (TARGET and GWC) — its own boxed field, matching League name's recipe */}
        {modeHasStartGw(selectedMode) && availableStartGws.length > 0 && (
          <div style={fieldBox}>
            <div style={{ ...eyebrow }}>{t("createLeague.config.startGw.label")}</div>
            <p style={{ margin: "0 0 0.625rem", fontSize: "0.82rem", color: "var(--color-text-muted)", lineHeight: 1.5 }}>
              {t("createLeague.config.startGw.desc")}
            </p>
            <select
              aria-label={t("createLeague.config.startGw.label")}
              value={startGameweekId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStartGameweekId(e.target.value)}
              style={{ ...selectStyle, maxWidth: "320px" }}
            >
              {availableStartGws.map((gw, i) => (
                <option key={gw.id} value={gw.id}>
                  {i === 0 ? `${gw.name} (${t("createLeague.config.startGw.defaultTag")})` : gw.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </>
    );
  }

  // ── Render: name section ──
  function renderName(): React.ReactElement {
    return (
      <div
        style={{
          ...card, marginBottom: "1.25rem",
          border: "1px solid var(--color-border-subtle)", padding: "1.25rem",
        }}
      >
        <div style={{ ...eyebrow }}>{t("createLeague.name.label")}</div>
        <input
          style={{
            ...inputStyle,
            ...(nameTouched && !name.trim() ? { borderColor: "var(--color-danger)" } : {}),
            fontSize: "1rem",
          }}
          placeholder={t("createLeague.name.placeholder")}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setName(e.target.value); if (nameTouched) setNameTouched(false); }}
          onBlur={() => setNameTouched(true)}
          maxLength={80}
        />
        {nameTouched && !name.trim() && (
          <p style={{ margin: "0.35rem 0 0", fontSize: "0.78rem", color: "var(--color-danger)" }}>
            {t("createLeague.errors.nameRequired")}
          </p>
        )}
      </div>
    );
  }

  // ── Render: Step 2 — Settings ──
  function renderStep2(): React.ReactElement {
    return (
      <div>
        <div style={{ ...eyebrow }}>{t("createLeague.step2.eyebrow")}</div>
        <h2
          style={{
            fontFamily: "var(--font-display)", fontSize: isMobile ? "1.4rem" : "1.7rem",
            fontWeight: 800, color: "var(--color-text)", margin: "0 0 1.25rem",
          }}
        >
          {t("createLeague.step2.heading")}
        </h2>
        {renderCompetition()}
        {renderConfig()}
        {renderName()}
      </div>
    );
  }

  // ── Render: Step 3 — Review ──
  function renderStep3(): React.ReactElement {
    const comp = competitions.find((c) => c.id === competitionId);
    const modeContent = selectedMode ? MODE_CONTENT[selectedMode] : null;
    const startGw = availableStartGws.find((gw) => gw.id === startGameweekId);

    const rows: Array<{ label: string; value: string; highlight?: boolean }> = [
      {
        label: t("createLeague.review.modeLabel"),
        value: modeContent ? t(`${modeContent.keyRoot}.label`) : "",
        highlight: true,
      },
      {
        label: t("createLeague.review.competitionLabel"),
        value: comp ? `${comp.name} · ${comp.season}` : "",
      },
      ...(selectedMode === "TARGET" ? [{
        label: t("createLeague.review.targetLabel"),
        value: String(target),
      }] : []),
      ...(selectedMode && modeHasStartGw(selectedMode) && startGw ? [{
        label: t("createLeague.review.startGwLabel"),
        value: startGw.name,
      }] : []),
      {
        label: t("createLeague.review.nameLabel"),
        value: name,
      },
    ];

    return (
      <div>
        <div style={{ ...eyebrow }}>{t("createLeague.step3.eyebrow")}</div>
        <h2
          style={{
            fontFamily: "var(--font-display)", fontSize: isMobile ? "1.4rem" : "1.7rem",
            fontWeight: 800, color: "var(--color-text)", margin: "0 0 1.25rem",
          }}
        >
          {t("createLeague.step3.heading")}
        </h2>
        <div
          style={{
            ...card,
            border: "1px solid var(--color-border-subtle)",
            padding: 0, overflow: "hidden",
            marginBottom: "1rem",
          }}
        >
          {rows.map(({ label, value, highlight }, i) => (
            <div
              key={label}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: "1rem", padding: "0.75rem 1rem",
                borderTop: i === 0 ? "none" : "1px solid var(--color-border-subtle)",
                background: highlight ? `color-mix(in srgb, ${modeColor} 8%, transparent)` : undefined,
              }}
            >
              <span style={{ fontSize: "0.78rem", color: "var(--color-text-dim)", flexShrink: 0 }}>{label}</span>
              <span style={{ fontSize: "0.875rem", fontWeight: 600, color: highlight ? modeColor : "var(--color-text)", textAlign: "right" as const }}>{value}</span>
            </div>
          ))}
        </div>
        {error && (
          <p style={{ ...errorStyle, marginBottom: "0.75rem" }}>{error}</p>
        )}
      </div>
    );
  }

  // ── Render: wizard footer ──
  function renderFooter(): React.ReactElement {
    const isStep3 = step === 3;
    const nextDisabled =
      (step === 1 && !step1Valid) ||
      (step === 2 && !step2Valid) ||
      (isStep3 && submitting);

    return (
      <div
        style={{
          display: "flex", alignItems: "center",
          justifyContent: step === 1 ? "flex-end" : "space-between",
          marginTop: "1.25rem", gap: "0.75rem",
        }}
      >
        {step > 1 && (
          <button
            onClick={goBack}
            style={{
              background: "none", border: "1px solid var(--color-border)",
              borderRadius: 8, cursor: "pointer",
              color: "var(--color-text-muted)", fontSize: "0.875rem", fontWeight: 500,
              padding: "0.65rem 1.1rem", fontFamily: "inherit",
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={(e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.borderColor = "var(--color-border-hover, var(--color-border))"; (e.currentTarget as HTMLElement).style.color = "var(--color-text)"; }}
            onMouseLeave={(e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.borderColor = "var(--color-border)"; (e.currentTarget as HTMLElement).style.color = "var(--color-text-muted)"; }}
          >
            {t("createLeague.nav.back")}
          </button>
        )}
        <button
          style={{
            ...primaryBtn,
            padding: "0.65rem 1.5rem",
            transition: "box-shadow 0.15s, transform 0.1s",
            opacity: nextDisabled ? 0.5 : 1,
            cursor: nextDisabled ? "not-allowed" : "pointer",
          }}
          onClick={isStep3 ? handleSubmit : goNext}
          disabled={nextDisabled}
          onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
            if (!nextDisabled) {
              (e.currentTarget as HTMLElement).style.boxShadow = "0 0 12px oklch(from var(--color-accent) l c h / 0.35)";
            }
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
            (e.currentTarget as HTMLElement).style.boxShadow = "none";
          }}
        >
          {submitting
            ? t("createLeague.create.saving")
            : isStep3
            ? t("createLeague.nav.create")
            : t("createLeague.nav.next")}
        </button>
      </div>
    );
  }

  // ── Guards ──
  if (delayedLoading) return <PageLoader rows={3} />;
  if (compLoading) return null;

  if (fetchError) {
    return (
      <div className="sg-fade-in" style={{ ...narrowPageContainer, padding: "1.5rem 0" }}>
        <div style={{ ...card, padding: "2.5rem 1.75rem", border: "1px solid var(--color-border-subtle)", textAlign: "center" as const }}>
          <div style={{ fontSize: "1.75rem", marginBottom: "0.75rem" }}>⚠️</div>
          <div style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", fontWeight: 700, color: "var(--color-text)", marginBottom: "0.5rem" }}>
            {t("createLeague.errors.fetchFailed")}
          </div>
          <button style={{ ...primaryBtn, marginTop: "1rem" }} onClick={doFetch}>
            {t("createLeague.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sg-fade-in" style={{ ...narrowPageContainer, padding: "1.5rem 0" }}>
      {/* Breadcrumb */}
      <div style={{ fontSize: "0.78rem", color: "var(--color-text-dim)", marginBottom: "1.5rem" }}>
        <Link
          to="/overview"
          style={{ color: "var(--color-text-muted)", textDecoration: "none" }}
          onMouseEnter={(e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.color = "var(--color-text)"; }}
          onMouseLeave={(e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.color = "var(--color-text-muted)"; }}
        >
          {t("common.home")}
        </Link>
        <span style={{ margin: "0 0.35rem", opacity: 0.4 }}>›</span>
        <span style={{ color: "var(--color-text)" }}>{t("createLeague.title")}</span>
      </div>

      {/* Stepper */}
      {renderStepper()}

      {/* Step card */}
      <div style={{ ...card, border: "1px solid var(--color-border-subtle)", padding: "1.5rem" }}>
        {step === 1 ? renderStep1() : step === 2 ? renderStep2() : renderStep3()}
      </div>

      {/* Footer */}
      {renderFooter()}

      {howToPlayMode && (
        <HowToPlayModal mode={howToPlayMode} onClose={() => setHowToPlayMode(null)} />
      )}
    </div>
  );
}
